import type {
  AffectedElementType,
  EventTargetPageType,
  EventTargetElementType,
  TimedEventActionType,
  TimedEventConfigType,
  DropdownOpenEventType,
  DropdownCloseEventType,
  NavbarOpenEventType,
  NavbarCloseEventType,
  SliderActiveEventType,
  SliderInactiveEventType,
  TabActiveEventType,
  TabInactiveEventType,
  MouseClickFirstEventType,
  MouseClickSecondEventType,
  MouseHoverOverEventType,
  MouseHoverOutEventType,
  ScrollIntoViewEventType,
  ScrollOutOfViewEventType,
  PageLoadStartEventType,
  PageLoadFinishEventType,
  PageScrolledUpEventType,
  PageScrolledDownEventType,
  ContinuousEventMouseConfigType,
  ContinuousEventScrollConfigType,
  ContinuousEventActionType,
  MouseMoveInElementEventType,
  ScrollingInViewEventType,
  MouseMoveInViewportEventType,
  WhilePageIsScrollingEventType,
  // Shared
  EventId,
  EventName,
} from '@packages/systems/ix2/types-core';

import {
  EventTypeConsts,
  ActionTypeConsts,
  EventAppliesTo,
  EventBasedOn,
  EventContinuousMouseAxes,
} from '@packages/systems/ix2/shared-constants';
import {
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
} from '@packages/systems/style/breakpoint-ids';

const {
  NAVBAR_OPEN,
  NAVBAR_CLOSE,
  TAB_ACTIVE,
  TAB_INACTIVE,
  SLIDER_ACTIVE,
  SLIDER_INACTIVE,
  DROPDOWN_OPEN,
  DROPDOWN_CLOSE,
  MOUSE_CLICK,
  MOUSE_SECOND_CLICK,
  MOUSE_OVER,
  MOUSE_OUT,
  MOUSE_MOVE,
  MOUSE_MOVE_IN_VIEWPORT,
  SCROLL_INTO_VIEW,
  SCROLL_OUT_OF_VIEW,
  SCROLLING_IN_VIEW,
  PAGE_START,
  PAGE_FINISH,
  PAGE_SCROLL_UP,
  PAGE_SCROLL_DOWN,
  PAGE_SCROLL,
} = EventTypeConsts;

const defaultMediaQueries = [
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
];

/**
 * Event Records
 */

export const defaultAffectedElement: AffectedElementType = {
  selector: null,
  limitAffectedElements: null,
};

export const defaultEventTargetConsts: {
  ELEMENT: EventTargetElementType;
  PAGE: EventTargetPageType;
  CLASS: EventTargetElementType;
} = {
  ELEMENT: {
    id: null,
    appliesTo: EventAppliesTo.ELEMENT,
    styleBlockIds: [],
  },

  PAGE: {
    id: null,
    appliesTo: EventAppliesTo.PAGE,
    styleBlockIds: [],
  },

  CLASS: {
    id: null,
    appliesTo: EventAppliesTo.CLASS,
    styleBlockIds: [],
  },
};

export const defaultEventConfigConsts: {
  TIMED: TimedEventConfigType;
  CONTINUOUS: {
    MOUSE: {
      ELEMENT: {
        X_AXIS: ContinuousEventMouseConfigType<
          typeof EventContinuousMouseAxes.X_AXIS,
          typeof EventBasedOn.ELEMENT
        >;
        Y_AXIS: ContinuousEventMouseConfigType<
          typeof EventContinuousMouseAxes.Y_AXIS,
          typeof EventBasedOn.ELEMENT
        >;
      };
      VIEWPORT: {
        X_AXIS: ContinuousEventMouseConfigType<
          typeof EventContinuousMouseAxes.X_AXIS,
          typeof EventBasedOn.VIEWPORT
        >;
        Y_AXIS: ContinuousEventMouseConfigType<
          typeof EventContinuousMouseAxes.Y_AXIS,
          typeof EventBasedOn.VIEWPORT
        >;
      };
    };
    SCROLL: ContinuousEventScrollConfigType;
  };
} = {
  TIMED: {
    loop: false,
    playInReverse: false,
    scrollOffsetValue: null,
    scrollOffsetUnit: null,
    delay: null,
    direction: null,
    effectIn: null,
  },

  CONTINUOUS: {
    MOUSE: {
      ELEMENT: {
        X_AXIS: {
          continuousParameterGroupId: null,
          selectedAxis: EventContinuousMouseAxes.X_AXIS,
          basedOn: EventBasedOn.ELEMENT,
          reverse: false,
          smoothing: 50,
          restingState: 50,
        },
        Y_AXIS: {
          continuousParameterGroupId: null,
          selectedAxis: EventContinuousMouseAxes.Y_AXIS,
          basedOn: EventBasedOn.ELEMENT,
          reverse: false,
          smoothing: 50,
          restingState: 50,
        },
      },

      VIEWPORT: {
        X_AXIS: {
          continuousParameterGroupId: null,
          selectedAxis: EventContinuousMouseAxes.X_AXIS,
          basedOn: EventBasedOn.VIEWPORT,
          reverse: false,
          smoothing: 50,
          restingState: 50,
        },
        Y_AXIS: {
          continuousParameterGroupId: null,
          selectedAxis: EventContinuousMouseAxes.Y_AXIS,
          basedOn: EventBasedOn.VIEWPORT,
          reverse: false,
          smoothing: 50,
          restingState: 50,
        },
      },
    },

    SCROLL: {
      continuousParameterGroupId: null,
      smoothing: 50,
      startsEntering: true,
      addStartOffset: false,
      addOffsetValue: 50,
      startsExiting: false,
      addEndOffset: false,
      endOffsetValue: 50,
    },
  },
};

const defaultEventId = '' as EventId;
const defaultEventName = 'Unnamed interaction' as EventName;

export const defaultEventActionConsts: {
  TIMED: TimedEventActionType;
  CONTINUOUS: ContinuousEventActionType;
} = {
  TIMED: {
    id: '',
    actionTypeId: null,
    config: {
      delay: 0,
      easing: '',
      duration: 500,
      actionListId: null,
      affectedElements: new Map(),
      playInReverse: false,
      autoStopEventId: defaultEventId,
    },
    instant: false, // tech debt
  },

  CONTINUOUS: {
    id: '',
    actionTypeId: ActionTypeConsts.GENERAL_CONTINUOUS_ACTION,
    config: {
      actionListId: null,
      affectedElements: new Map(),
    },
    instant: true,
  },
};

/**
 * Timed Events
 */

const defaultDropdownOpenEvent: DropdownOpenEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: DROPDOWN_OPEN,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultDropdownCloseEvent: DropdownCloseEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: DROPDOWN_CLOSE,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultNavbarOpenEvent: NavbarOpenEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: NAVBAR_OPEN,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultNavbarCloseEvent: NavbarCloseEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: NAVBAR_CLOSE,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultSliderActiveEvent: SliderActiveEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: SLIDER_ACTIVE,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultSliderInactiveEvent: SliderInactiveEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: SLIDER_INACTIVE,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultTabActiveEvent: TabActiveEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: TAB_ACTIVE,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultTabInactiveEvent: TabInactiveEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: TAB_INACTIVE,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultMouseClickFirstEvent: MouseClickFirstEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: MOUSE_CLICK,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultMouseClickSecondEvent: MouseClickSecondEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: MOUSE_SECOND_CLICK,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultMouseHoverOverEvent: MouseHoverOverEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: MOUSE_OVER,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultMouseHoverOutEvent: MouseHoverOutEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: MOUSE_OUT,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultScrollIntoViewEvent: ScrollIntoViewEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: SCROLL_INTO_VIEW,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: {
    ...defaultEventConfigConsts.TIMED,
    scrollOffsetUnit: '%',
    scrollOffsetValue: 0,
  },
  createdOn: null,
};

const defaultScrollOutOfViewEvent: ScrollOutOfViewEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'preset',
  eventTypeId: SCROLL_OUT_OF_VIEW,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: {
    ...defaultEventConfigConsts.TIMED,
    scrollOffsetUnit: '%',
    scrollOffsetValue: 0,
  },
  createdOn: null,
};

/**
 * Timed Page Events
 */

const defaultPageLoadStartEvent: PageLoadStartEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'custom',
  eventTypeId: PAGE_START,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.PAGE,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultPageLoadFinishEvent: PageLoadFinishEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'custom',
  eventTypeId: PAGE_FINISH,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.PAGE,
  targets: [],
  config: defaultEventConfigConsts.TIMED,
  createdOn: null,
};

const defaultPageScrolledUpEvent: PageScrolledUpEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'custom',
  eventTypeId: PAGE_SCROLL_UP,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.PAGE,
  targets: [],
  config: {
    ...defaultEventConfigConsts.TIMED,
    scrollOffsetUnit: '%',
    scrollOffsetValue: 0,
  },
  createdOn: null,
};

const defaultPageScrolledDownEvent: PageScrolledDownEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'custom',
  eventTypeId: PAGE_SCROLL_DOWN,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.PAGE,
  targets: [],
  config: {
    ...defaultEventConfigConsts.TIMED,
    scrollOffsetUnit: '%',
    scrollOffsetValue: 0,
  },
  createdOn: null,
};

/**
 * Continuous Events
 */

const defaultMouseMoveInElementEvent: MouseMoveInElementEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'custom',
  eventTypeId: MOUSE_MOVE,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: [
    defaultEventConfigConsts.CONTINUOUS.MOUSE.ELEMENT.X_AXIS,
    defaultEventConfigConsts.CONTINUOUS.MOUSE.ELEMENT.Y_AXIS,
  ],
  createdOn: null,
};

const defaultScrollingInViewEvent: ScrollingInViewEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'custom',
  eventTypeId: SCROLLING_IN_VIEW,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.ELEMENT,
  targets: [],
  config: [defaultEventConfigConsts.CONTINUOUS.SCROLL],
  createdOn: null,
};

/**
 * Continuous Page Events
 */

const defaultMouseMoveInViewportEvent: MouseMoveInViewportEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'custom',
  eventTypeId: MOUSE_MOVE,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.PAGE,
  targets: [],
  config: [
    defaultEventConfigConsts.CONTINUOUS.MOUSE.VIEWPORT.X_AXIS,
    defaultEventConfigConsts.CONTINUOUS.MOUSE.VIEWPORT.Y_AXIS,
  ],
  createdOn: null,
};

const defaultWhilePageIsScrollingEvent: WhilePageIsScrollingEventType = {
  id: defaultEventId,
  name: defaultEventName,
  animationType: 'custom',
  eventTypeId: PAGE_SCROLL,
  action: null,
  mediaQueries: defaultMediaQueries,
  target: defaultEventTargetConsts.PAGE,
  targets: [],
  config: [defaultEventConfigConsts.CONTINUOUS.SCROLL],
  createdOn: null,
};

export const EventRecords = {
  [DROPDOWN_OPEN]: defaultDropdownOpenEvent,
  [DROPDOWN_CLOSE]: defaultDropdownCloseEvent,
  [NAVBAR_OPEN]: defaultNavbarOpenEvent,
  [NAVBAR_CLOSE]: defaultNavbarCloseEvent,
  [SLIDER_ACTIVE]: defaultSliderActiveEvent,
  [SLIDER_INACTIVE]: defaultSliderInactiveEvent,
  [TAB_ACTIVE]: defaultTabActiveEvent,
  [TAB_INACTIVE]: defaultTabInactiveEvent,
  [MOUSE_CLICK]: defaultMouseClickFirstEvent,
  [MOUSE_SECOND_CLICK]: defaultMouseClickSecondEvent,
  [MOUSE_OVER]: defaultMouseHoverOverEvent,
  [MOUSE_OUT]: defaultMouseHoverOutEvent,
  [SCROLL_INTO_VIEW]: defaultScrollIntoViewEvent,
  [SCROLL_OUT_OF_VIEW]: defaultScrollOutOfViewEvent,
  [PAGE_START]: defaultPageLoadStartEvent,
  [PAGE_FINISH]: defaultPageLoadFinishEvent,
  [PAGE_SCROLL_UP]: defaultPageScrolledUpEvent,
  [PAGE_SCROLL_DOWN]: defaultPageScrolledDownEvent,
  [MOUSE_MOVE]: defaultMouseMoveInElementEvent,
  [SCROLLING_IN_VIEW]: defaultScrollingInViewEvent,
  [MOUSE_MOVE_IN_VIEWPORT]: defaultMouseMoveInViewportEvent,
  [PAGE_SCROLL]: defaultWhilePageIsScrollingEvent,
} as const;
